import { CTABand } from "../../components/site/CTABand";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import { SiteHeader } from "../../components/site/SiteHeader";
import { SiteFooter } from "../../components/site/SiteFooter";
import { PageHero } from "../../components/site/PageHero";
import { ContactAside } from "../../components/EnquiryForm";
import { Reveal } from "../../components/motion/Reveal";
import { SOLUTION_DETAILS } from "../../data/solutions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const label = "block text-xs tracking-[0.18em] uppercase text-slate-400 mb-2";
const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";

export default function RequestQuote() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const product = params.get("product") || "";
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", location: "", solution: "", requirement: product ? `Interested in: ${product} — please share pricing and availability.` : "", project_size: "", timeline: "", budget: "", existing_system: "", message: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Document must be smaller than 10 MB."); return; }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = new FormData();
      Object.entries(form).forEach(([k, v]) => payload.append(k, v));
      if (file) payload.append("documents", file);
      await axios.post(`${API}/quotes`, payload, { headers: { "Content-Type": "multipart/form-data" } });
      navigate("/thank-you?type=quote");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request. Please email sales@nexsecure.in");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased" data-testid="quote-page">
      <SiteHeader />
      <PageHero
        crumb="Request a Quote"
        eyebrow="Request a Quote"
        title="A detailed quote,"
        titleAccent="engineered properly."
        description="For defined projects: share the requirement, scale and any drawings or BOQs. The more we know, the sharper the proposal."
      />
      <section className="py-16 sm:py-24">
        <div className="nx-wrap grid lg:grid-cols-[1.7fr_1fr] gap-14">
          <Reveal>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="quote-form">
              <div><label htmlFor="q-name" className={label}>Name *</label><input id="q-name" data-testid="quote-name" required className={input} value={form.name} onChange={set("name")} placeholder="Your full name" /></div>
              <div><label htmlFor="q-company" className={label}>Company</label><input id="q-company" data-testid="quote-company" className={input} value={form.company} onChange={set("company")} placeholder="Organisation" /></div>
              <div><label htmlFor="q-email" className={label}>Email *</label><input id="q-email" data-testid="quote-email" required type="email" className={input} value={form.email} onChange={set("email")} placeholder="you@company.com" /></div>
              <div><label htmlFor="q-phone" className={label}>Phone *</label><input id="q-phone" data-testid="quote-phone" required type="tel" className={input} value={form.phone} onChange={set("phone")} placeholder="+91" /></div>
              <div><label htmlFor="q-location" className={label}>Location *</label><input id="q-location" data-testid="quote-location" required className={input} value={form.location} onChange={set("location")} placeholder="City / site location" /></div>
              <div>
                <label htmlFor="q-solution" className={label}>Solution *</label>
                <select id="q-solution" data-testid="quote-solution" required className={input} value={form.solution} onChange={set("solution")}>
                  <option value="" disabled className="text-neutral-900">Select a solution area</option>
                  {SOLUTION_DETAILS.map((s) => <option key={s.slug} value={s.name} className="text-neutral-900">{s.name}</option>)}
                  <option value="Managed Services / AMC" className="text-neutral-900">Managed Services / AMC</option>
                  <option value="Software Services" className="text-neutral-900">Software Services</option>
                  <option value="Integrated Solutions" className="text-neutral-900">Integrated Solutions</option>
                  <option value="Other" className="text-neutral-900">Other</option>
                </select>
              </div>
              <div className="sm:col-span-2"><label htmlFor="q-req" className={label}>Requirement *</label><textarea id="q-req" data-testid="quote-requirement" required rows={3} className={input} value={form.requirement} onChange={set("requirement")} placeholder="What do you need? Systems, sites, scale — as you understand it." /></div>
              <div><label htmlFor="q-size" className={label}>Project Size</label><input id="q-size" data-testid="quote-size" className={input} value={form.project_size} onChange={set("project_size")} placeholder="e.g. 3 floors, 40 cameras, 200 users" /></div>
              <div><label htmlFor="q-timeline" className={label}>Timeline</label><input id="q-timeline" data-testid="quote-timeline" className={input} value={form.timeline} onChange={set("timeline")} placeholder="e.g. within 2 months" /></div>
              <div><label htmlFor="q-budget" className={label}>Budget (optional)</label><input id="q-budget" data-testid="quote-budget" className={input} value={form.budget} onChange={set("budget")} placeholder="Indicative range, if known" /></div>
              <div>
                <label htmlFor="q-existing" className={label}>Existing System?</label>
                <select id="q-existing" data-testid="quote-existing" className={input} value={form.existing_system} onChange={set("existing_system")}>
                  <option value="" className="text-neutral-900">Not sure</option>
                  <option value="Yes" className="text-neutral-900">Yes — upgrade / expansion</option>
                  <option value="No" className="text-neutral-900">No — new installation</option>
                </select>
              </div>
              <div className="sm:col-span-2"><label htmlFor="q-msg" className={label}>Additional Notes</label><textarea id="q-msg" data-testid="quote-message" rows={3} className={input} value={form.message} onChange={set("message")} placeholder="Anything else we should know" /></div>
              <div className="sm:col-span-2">
                <label className={label}>Drawings / BOQ / Documents (optional, max 10 MB)</label>
                {file ? (
                  <div className="flex items-center justify-between border border-nx-gold/40 bg-nx-gold/[0.06] px-4 py-3" data-testid="quote-file-attached">
                    <span className="flex items-center gap-3 text-sm text-slate-200 truncate"><Paperclip size={15} className="text-nx-goldlight shrink-0" /> {file.name}</span>
                    <button type="button" onClick={() => setFile(null)} className="text-slate-400 hover:text-white p-1" aria-label="Remove file" data-testid="quote-file-remove"><X size={16} /></button>
                  </div>
                ) : (
                  <label htmlFor="q-file" data-testid="quote-file-upload" className="flex items-center justify-center gap-3 border border-dashed border-white/20 hover:border-nx-gold/50 text-slate-400 hover:text-slate-200 px-4 py-6 text-sm cursor-pointer transition-colors">
                    <Paperclip size={16} className="text-nx-goldlight" /> Attach floor plans, BOQ, diagrams or photos
                    <input id="q-file" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} data-testid="quote-file-input" />
                  </label>
                )}
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={sending} data-testid="quote-submit" className="w-full sm:w-auto bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                  {sending ? "Sending…" : "Request a Quote"}
                </button>
              </div>
            </form>
          </Reveal>
          <div className="lg:pt-2">
            <Reveal delay={0.15}>
              <div className="border border-white/10 bg-white/[0.03] p-8">
                <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer to reach us directly?</p>
                <ContactAside tone="dark" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
      <SiteFooter />
    </div>
  );
}
